import type { CSSProperties } from 'react';

/**
 * Layout math for N video-call tiles on a bounded area.
 *
 * Design:
 * - Every tile is the same *square* size (so no participant looks "bigger"
 *   than another — similar to Google Meet / Zoom speaker-off views).
 * - When given the container's width and height, pick the `cols × rows`
 *   that maximizes the tile edge. On a wide desktop area this tends to
 *   produce side-by-side tiles; on a tall mobile area the same call
 *   produces stacked tiles.
 * - When called without dimensions (SSR / unit tests / first render
 *   before measurement), fall back to a mobile-friendly default that
 *   matches the tall portrait viewport.
 * - Rendering uses flex rows (computed via `getTileRows`): a full grid
 *   `cols × rows` with any partial last row rendered as its own flex row
 *   whose children are the remaining tiles, centered with `justify-center`.
 *   This keeps the markup simple and every tile truly square.
 */
export interface TileGridLayout {
  cols: number;
  rows: number;
}

/** Inter-tile gap (px). Used both visually and in layout math. */
export const TILE_GAP_PX = 12;

function fallbackLayout(count: number): TileGridLayout {
  if (count === 2) return { cols: 1, rows: 2 };
  const cols = Math.max(1, Math.ceil(Math.sqrt(count)));
  return { cols, rows: Math.max(1, Math.ceil(count / cols)) };
}

function computeSquareEdge(
  cols: number,
  rows: number,
  containerWidth: number,
  containerHeight: number,
): number {
  const availW = containerWidth - TILE_GAP_PX * Math.max(0, cols - 1);
  const availH = containerHeight - TILE_GAP_PX * Math.max(0, rows - 1);
  return Math.max(0, Math.min(availW / cols, availH / rows));
}

function isValidDim(v: number | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0;
}

export function getTileGridLayout(
  total: number,
  containerWidth?: number,
  containerHeight?: number,
): TileGridLayout {
  const count = Math.max(1, total);
  if (!isValidDim(containerWidth) || !isValidDim(containerHeight)) {
    return fallbackLayout(count);
  }
  let best: { cols: number; rows: number; size: number } = {
    cols: 1,
    rows: count,
    size: 0,
  };
  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    const size = computeSquareEdge(cols, rows, containerWidth, containerHeight);
    if (size > best.size) {
      best = { cols, rows, size };
    }
  }
  return { cols: best.cols, rows: best.rows };
}

/** Largest square tile edge (px) for the given layout + container. */
export function getTileEdgePx(
  total: number,
  containerWidth?: number,
  containerHeight?: number,
): number | null {
  if (!isValidDim(containerWidth) || !isValidDim(containerHeight)) return null;
  const { cols, rows } = getTileGridLayout(total, containerWidth, containerHeight);
  return computeSquareEdge(cols, rows, containerWidth, containerHeight);
}

/**
 * Split `total` tile indices into rows (arrays of tile indices) following the
 * computed layout. Example: total=5 with cols=3, rows=2 -> [[0,1,2],[3,4]].
 */
export function getTileRows(
  total: number,
  containerWidth?: number,
  containerHeight?: number,
): number[][] {
  const count = Math.max(0, total);
  if (count === 0) return [];
  const { cols } = getTileGridLayout(count, containerWidth, containerHeight);
  const rows: number[][] = [];
  for (let i = 0; i < count; i += cols) {
    const row: number[] = [];
    for (let j = 0; j < cols && i + j < count; j++) {
      row.push(i + j);
    }
    rows.push(row);
  }
  return rows;
}

/**
 * Style for a tile. When container dims are known, the tile is a hard
 * `edge × edge` px square. Otherwise it's a fluid square via
 * `aspect-ratio: 1 / 1`.
 */
export function getTileStyle(
  total: number,
  containerWidth?: number,
  containerHeight?: number,
): CSSProperties {
  const edge = getTileEdgePx(total, containerWidth, containerHeight);
  if (edge === null) {
    return { aspectRatio: '1 / 1', width: '100%', maxWidth: '100%' };
  }
  return { width: `${edge}px`, height: `${edge}px` };
}
